import React from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AuthenticationContextProvider } from './modules/auth/contexts/AuthenticationContext';
import { StubAuthProvider } from './modules/auth/contexts/StubAuthProvider';
import { ActiveOrgProvider } from './modules/auth/contexts/ActiveOrgContext';
import { ImpersonationProvider } from './modules/auth/contexts/ImpersonationContext';
import { ConfirmProvider } from './shared/components/ConfirmProvider';
import GlobalErrorSnackbar from './shared/components/GlobalErrorSnackbar';
import { queryClient } from './shared/api/queryClient';

const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN || '';
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '';
const auth0Audience = import.meta.env.VITE_AUTH0_AUDIENCE || '';

// Dev-only Auth0 bypass. Two gates so this can never reach prod:
//   1. import.meta.env.DEV is false in any production build (Vite tree-shakes the branch)
//   2. VITE_E2E_STUB must be explicitly "true" in a .env.* file
// Set VITE_E2E_STUB=true in wicklander-frontend/.env.development.local to enable.
const E2E_STUB_ACTIVE = import.meta.env.DEV && import.meta.env.VITE_E2E_STUB === 'true';

export default function ContextsProvider({ children }: { children: React.ReactNode }) {
  if (E2E_STUB_ACTIVE) {
     
    console.warn('[E2E_STUB] Auth0 bypassed — impersonating a seeded user.');
    return (
      <QueryClientProvider client={queryClient}>
        <StubAuthProvider>
          <ActiveOrgProvider>
            <ImpersonationProvider>
              <ConfirmProvider>{children}</ConfirmProvider>
            </ImpersonationProvider>
          </ActiveOrgProvider>
        </StubAuthProvider>
        <GlobalErrorSnackbar />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    );
  }

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: auth0Audience,
        scope: 'openid profile email offline_access',
      }}
    >
      <QueryClientProvider client={queryClient}>
        <AuthenticationContextProvider>
          <ActiveOrgProvider>
            <ImpersonationProvider>
              <ConfirmProvider>{children}</ConfirmProvider>
            </ImpersonationProvider>
          </ActiveOrgProvider>
        </AuthenticationContextProvider>
        <GlobalErrorSnackbar />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Auth0Provider>
  );
}
